import axios from 'axios';
import { defineStore } from 'pinia';

import getEnv from '@/utils/env';
import { getPreferredLocale } from '@/utils/locale';
import { Schema, Method, Action } from '@/types/general';
import { EnvironmentTypes } from '@/types/environmentTypes';

/**
 * Add the schemaUris that contains localized string in this array.
 * They will be cleared on profile language switch.
 */
const LOCALE_DEPENDENT_SCHEMAS = [
  '/transactions/advanced-search',
  '/transactions/1/customize', // TODO: delete after 1-2 releases
  '/v2/transactions/1/customize',
  '/extension/partnerships/declarations',
  'extension/partnerships/declarations/1'
];

export interface ApiResource {
  path: string;
  host?: string;
}

type Schemas = {
  schemas: {
    [key: string]: Schema;
  };
};
export const useSchemasStore = defineStore('schemas', {
  state: (): Schemas => ({
    schemas: {}
  }),
  getters: {},
  actions: {
    resetSchema(key: string) {
      delete this.schemas[key];
    },
    resetSchemasWithLocalization() {
      LOCALE_DEPENDENT_SCHEMAS.forEach((schema) => {
        delete this.schemas[schema];
      });
    },
    resetSchemasWithCid() {
      /**
       * Instead of having a list of schemas with CID that we need to keep up to date and removing them on logout, we can just go through all the schemas
       * that contain a uuid in their uri and delete all of them.
       */
      const schemasWithCid = Object.keys(this.schemas).filter((key) =>
        key.match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/g)
      );
      schemasWithCid.forEach((schemaKey) => delete this.schemas[schemaKey]);
    },
    async getSchemaForAction(action: Action) {
      return await this.getSchema({ path: action.uri }, action.method);
    },
    async getSchema(apiResource: ApiResource, schemaMethod = Method.post) {
      if (this.schemas[apiResource.path]) {
        return this.schemas[apiResource.path];
      }
      const host = apiResource.host ?? getEnv(EnvironmentTypes.VITE_APP_GW_HOST);

      const resp = await axios({
        method: 'get',
        url: `${host}/${schemaMethod}-schema/api${apiResource.path}`,
        headers: {
          'x-api-key': getEnv(EnvironmentTypes.VITE_APP_API_KEY),
          'Accept-Language': getPreferredLocale()
        }
      });

      this.schemas[apiResource.path] = resp.data;
      return this.schemas[apiResource.path];
    }
  }
});
